// Tab "Chữ ký điện tử": văn bản chờ ký, văn bản đã ký (xem, kiểm tra toàn vẹn, in lại, thu hồi)
import { useRef, useState } from 'react';
import { Input, Modal, Segmented, Spin, Table, message } from 'antd';
import { BadgeCheck, FilePlus2, FileSignature, Printer, ShieldX, XCircle } from 'lucide-react';
import { branchName } from '../POS/data';
import { useTreatmentAuth } from '../Treatment/actions';
import { Pill, fmtDateTime } from '../Treatment/ui';
import { SIGN_KIND, type SignDoc } from './data';
import { digest, getMedia, useMedia } from './mediaDb';
import useC360Store from './store';
import { printDoc } from './templates';
import { Btn, Section, type C360Ops } from './ui360';
import type { C360 } from './useCustomer360';

export default function SignPanel({ c, ops, onView }: { c: C360; ops: C360Ops; onView: (id: string) => void }) {
  const { can } = useTreatmentAuth();
  const [kind, setKind] = useState<'all' | SignDoc['kind']>('all');
  const docs = c.data.docs.filter(d => kind === 'all' || d.kind === kind).sort((a, b) => b.signedAt.localeCompare(a.signedAt));
  const paper = c.visits.filter(v => v.status === 'paper').length;

  return (
    <div className="space-y-4">
      <Section title={<span className="flex items-center gap-2">Chờ khách ký {c.needs.length > 0 && <span className="rounded-full bg-red-500 px-2 text-xs text-white">{c.needs.length}</span>}</span>}
        extra={can('create') && <Btn primary onClick={() => ops.sign({ kind: 'consent' })}><FilePlus2 className="h-4 w-4" /> Tạo văn bản ký</Btn>}>
        {c.needs.length ? (
          <div className="divide-y divide-slate-100">
            {c.needs.map(n => (
              <div key={n.key} className="flex items-center gap-3 py-2">
                <FileSignature className={`h-4 w-4 shrink-0 ${n.overdue ? 'text-red-600' : 'text-amber-600'}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">{n.title}</div>
                  <div className="truncate text-xs text-slate-500">{n.hint}</div>
                </div>
                {can('create') && <Btn primary={n.overdue} onClick={() => (n.kind === 'visit' && n.visitDate ? ops.signVisit(n.visitDate) : ops.sign({ kind: n.kind, cardId: n.cardId, usageId: n.usageId }))}>Ký ngay</Btn>}
              </div>
            ))}
          </div>
        ) : <div className="py-3 text-center text-sm text-slate-400">Không có văn bản chờ ký</div>}
        {paper > 0 && <div className="mt-2 text-xs text-slate-500">{paper} lần đến khách ký trên phiếu giấy (không có bản điện tử) – xem ở tab Lần đến.</div>}
      </Section>

      <Section title="Văn bản đã ký" extra={
        <Segmented size="small" value={kind} onChange={v => setKind(v as typeof kind)}
          options={[{ value: 'all', label: 'Tất cả' }, ...(Object.keys(SIGN_KIND) as SignDoc['kind'][]).map(k => ({ value: k, label: SIGN_KIND[k].short }))]} />
      }>
        <Table<SignDoc> rowKey="id" size="small" className="custom-ant-table" dataSource={docs} pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Chưa có văn bản ký' }} onRow={d => ({ onClick: () => onView(d.id), className: 'cursor-pointer' })}
          columns={[
            { title: 'Số', dataIndex: 'id', width: 100, render: v => <span className="font-mono text-xs">{v}</span> },
            { title: 'Văn bản', render: (_, d) => <span>{d.title}<div className="text-xs text-slate-400">{SIGN_KIND[d.kind].short}{d.cardId ? ` · ${d.cardId}` : ''}</div></span> },
            { title: 'Ký lúc', width: 140, render: (_, d) => <span>{fmtDateTime(d.signedAt)}<div className="text-xs text-slate-400">{branchName(d.branchId)}</div></span> },
            { title: 'Người ký / chứng kiến', render: (_, d) => <span>{d.signerName}<div className="text-xs text-slate-400">NV: {d.witness}</div></span> },
            { title: 'Trạng thái', width: 110, render: (_, d) => d.status === 'signed' ? <Pill label="Hiệu lực" cls="bg-green-50 text-green-700 border-green-200" /> : <Pill label="Đã thu hồi" cls="bg-red-50 text-red-600 border-red-200" title={d.revoked?.reason} /> },
          ]} />
      </Section>
    </div>
  );
}

/** Xem văn bản đã ký: nội dung chụp lúc ký + chữ ký + kiểm tra mã xác thực */
export function DocViewer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const doc = useC360Store(s => s.docs.find(d => d.id === id));
  const revokeDoc = useC360Store(s => s.revokeDoc);
  const markPrinted = useC360Store(s => s.markPrinted);
  const sig = useMedia(doc?.signatureId);
  const { can, userName } = useTreatmentAuth();
  const [verify, setVerify] = useState<'idle' | 'ok' | 'bad' | 'checking'>('idle');
  const reason = useRef('');

  if (!doc) return null;

  const check = async () => {
    setVerify('checking');
    const s = await getMedia(doc.signatureId);
    const h = await digest(JSON.stringify({ sections: doc.sections, signerName: doc.signerName, signerPhone: doc.signerPhone, signedAt: doc.signedAt }) + (s || ''));
    setVerify(h === doc.hash ? 'ok' : 'bad');
  };
  const print = async () => {
    printDoc(doc, await getMedia(doc.signatureId));
    markPrinted(doc.id);
  };
  const revoke = () => Modal.confirm({
    title: `Thu hồi ${doc.id}?`, okText: 'Thu hồi', okButtonProps: { danger: true }, cancelText: 'Không',
    content: <div className="space-y-2"><p className="text-sm text-slate-600">Văn bản vẫn được lưu để đối chiếu nhưng không còn hiệu lực (VD: ký nhầm thẻ, khách rút lại đồng ý).</p><Input placeholder="Lý do thu hồi" onChange={e => { reason.current = e.target.value; }} /></div>,
    onOk: () => {
      if (reason.current.trim().length < 5) { message.error('Nhập lý do tối thiểu 5 ký tự'); return Promise.reject(); }
      revokeDoc(doc.id, userName, reason.current.trim());
      reason.current = '';
      return undefined;
    },
  });

  return (
    <Modal open={!!id} onCancel={() => { setVerify('idle'); onClose(); }} centered width={700} destroyOnHidden
      title={<span className="flex items-center gap-2">{doc.title} <span className="font-mono text-xs font-normal text-slate-400">{doc.id}</span></span>}
      footer={
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex items-center gap-2">
            <Btn onClick={check}>{verify === 'checking' ? <Spin size="small" /> : <BadgeCheck className="h-4 w-4" />} Kiểm tra toàn vẹn</Btn>
            {verify === 'ok' && <span className="flex items-center gap-1 text-xs font-semibold text-green-700"><BadgeCheck className="h-4 w-4" /> Khớp mã – chưa bị sửa</span>}
            {verify === 'bad' && <span className="flex items-center gap-1 text-xs font-semibold text-red-600"><ShieldX className="h-4 w-4" /> Không khớp mã!</span>}
          </div>
          <div className="flex gap-2">
            {doc.status === 'signed' && can('approve') && <Btn danger onClick={revoke}><XCircle className="h-4 w-4" /> Thu hồi</Btn>}
            <Btn primary onClick={print}><Printer className="h-4 w-4" /> {doc.printCount ? 'In lại' : 'In'}</Btn>
          </div>
        </div>
      }>
      {doc.status === 'revoked' && doc.revoked && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">Đã thu hồi bởi {doc.revoked.by} lúc {fmtDateTime(doc.revoked.at)}: {doc.revoked.reason}</div>
      )}
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
        {doc.sections.map(s => (
          <div key={s.heading} className="mb-2">
            <div className="font-semibold text-slate-700">{s.heading}</div>
            {s.lines.map((l, i) => <div key={i} className="text-slate-600">{l}</div>)}
          </div>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="text-xs text-slate-500">
          <div>Ký lúc <b className="text-slate-700">{fmtDateTime(doc.signedAt)}</b> tại {branchName(doc.branchId)}</div>
          <div>Người ký: <b className="text-slate-700">{doc.signerName}</b> · NV chứng kiến: {doc.witness}</div>
          <div>Đã in {doc.printCount} lần</div>
          <div className="mt-1 break-all font-mono text-[10px] text-slate-400">SHA-256: {doc.hash}</div>
        </div>
        <div className="flex h-28 items-center justify-center rounded-lg border border-slate-200 bg-white">
          {sig ? <img src={sig} alt="Chữ ký" className="max-h-24 max-w-full" /> : <Spin size="small" />}
        </div>
      </div>
    </Modal>
  );
}
